<template>
  <div id="rate-hotel-events" class="d-flex rate-data-row">
    <div class="item rate-labels semibold">
      <div class="simple_view text-uppercase">
        <v-icon small>mdi-calendar-star</v-icon>
        {{ $t("events") }}
      </div>
    </div>
    <div class="item days">
      <div
        class="days-wrapper"
        :class="'x' + days_per_screen"
        v-if="days.length > 0"
      >
        <div
          v-for="(day, index) in days"
          :key="'hotel-event-' + day"
          class="day d-flex flex-column justify-center"
          :class="{ weekend: isWeekend(day) }"
          @mouseover="
            date_hover = day;
            day_hover = index;
          "
          @mouseleave="
            date_hover = '';
            day_hover = '';
          "
        >
          <div
            v-if="mustRender(day)"
            class="day-date"
            :class="'visible-day visible-day-' + getDayIndexInRendered(day)"
          >
            <div class="d-flex align-center justify-center" style="gap: 2px">
              <!-- Evento más importante -->
              <v-tooltip
                v-if="getMostImportantEvent(day)"
                :key="getMostImportantEvent(day).id + '-' + day"
                content-class="a-rate-tooltip text-left"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    :color="getImpactColor(getMostImportantEvent(day).impact)"
                    text-color="white"
                    class="event-chip"
                    x-small
                    dark
                  >
                    {{ truncateEventName(getMostImportantEvent(day).name) }}
                  </v-chip>
                </template>
                <div class="event-tooltip-content">
                  <div class="font-weight-bold mb-1">
                    {{ getMostImportantEvent(day).name }}
                  </div>
                  <div class="mb-1">
                    <v-icon x-small class="mr-1" color="black">
                      mdi-calendar
                    </v-icon>
                    {{ getEventDateRange(getMostImportantEvent(day)) }}
                  </div>
                  <div class="mb-1">
                    <v-icon x-small class="mr-1" color="black">
                      mdi-chart-line
                    </v-icon>
                    {{ $t("impact") }}:
                    {{ formatImpact(getMostImportantEvent(day).impact) }}
                  </div>
                  <div>
                    <v-icon x-small class="mr-1" color="black">
                      {{ getEventTypeIcon(getMostImportantEvent(day).type) }}
                    </v-icon>
                    {{ $t("type") }}:
                    {{ formatType(getMostImportantEvent(day).type) }}
                  </div>
                </div>
              </v-tooltip>

              <!-- Contador de eventos adicionales -->
              <v-tooltip
                v-if="getAdditionalEventsCount(day) > 0"
                content-class="a-rate-tooltip text-left"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-avatar
                    v-bind="attrs"
                    v-on="on"
                    size="16"
                    :color="
                      getImpactColor(
                        getMostImportantAdditionalEvent(day).impact
                      )
                    "
                    class="white--text counter"
                  >
                    +{{ getAdditionalEventsCount(day) }}
                  </v-avatar>
                </template>
                <div class="event-tooltip-content">
                  <div class="font-weight-bold mb-2">
                    {{ $t("additional_events") }}:
                  </div>
                  <div
                    v-for="event in getAdditionalEvents(day)"
                    :key="event.id"
                    class="mb-1 d-flex align-center"
                    style="gap: 8px"
                  >
                    <v-avatar size="20" :color="getImpactColor(event.impact)">
                      <v-icon x-small color="white">
                        {{ getEventTypeIcon(event.type) }}
                      </v-icon>
                    </v-avatar>
                    <span
                      >{{ event.name }} - {{ formatImpact(event.impact) }}</span
                    >
                  </div>
                </div>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapState } from "vuex";

export default {
  name: "rate-hotel-events",
  props: {
    days_to_render: {
      type: Array,
      required: true
    },
    days: {
      type: Array,
      required: true
    },
    hotel_events: {
      type: Array,
      default: () => []
    },
    days_per_screen: {
      type: Number,
      required: true
    }
  },
  computed: {
    processedEvents() {
      let eventsToProcess = [];

      /* // Siempre añadir eventos de ejemplo al principio para desarrollo
      const today = Vue.moment();
      const exampleEvents = [
        {
          id: "example-1",
          name: "Festival Internacional de Música",
          start_date: today.format("Y-MM-DD"),
          end_date: today
            .clone()
            .add(3, "days")
            .format("Y-MM-DD"),
          type: "entertainment",
          impact: "high"
        },
        {
          id: "example-2",
          name: "Convención de Tecnología",
          start_date: today
            .clone()
            .add(1, "days")
            .format("Y-MM-DD"),
          end_date: today
            .clone()
            .add(4, "days")
            .format("Y-MM-DD"),
          type: "convention",
          impact: "very_high"
        },
        {
          id: "example-3",
          name: "Maratón Ciudad",
          start_date: today
            .clone()
            .add(2, "days")
            .format("Y-MM-DD"),
          end_date: today
            .clone()
            .add(2, "days")
            .format("Y-MM-DD"),
          type: "sports",
          impact: "medium"
        },
        {
          id: "example-4",
          name: "Feria de Artesanía Local",
          start_date: today
            .clone()
            .add(5, "days")
            .format("Y-MM-DD"),
          end_date: today
            .clone()
            .add(7, "days")
            .format("Y-MM-DD"),
          type: "fair",
          impact: "low"
        },
        {
          id: "example-5",
          name: "Concierto Estrella Mundial",
          start_date: today
            .clone()
            .add(6, "days")
            .format("Y-MM-DD"),
          end_date: today
            .clone()
            .add(6, "days")
            .format("Y-MM-DD"),
          type: "entertainment",
          impact: "exceptional"
        }
      ];

      // Combinar eventos de ejemplo con eventos reales
      eventsToProcess = [...exampleEvents]; */

      if (this.hotel_events && Array.isArray(this.hotel_events)) {
        eventsToProcess = [...eventsToProcess, ...this.hotel_events];
      }

      // Procesar eventos para expandir los que duran múltiples días
      const expandedEvents = [];

      eventsToProcess.forEach(event => {
        const startDate = Vue.moment(event.start_date);
        const endDate = Vue.moment(event.end_date);
        const duration = endDate.diff(startDate, "days") + 1;

        // Crear una entrada para cada día del evento
        for (let i = 0; i < duration; i++) {
          const currentDate = startDate.clone().add(i, "days");
          const dateStr = currentDate.format("Y-MM-DD");

          expandedEvents.push({
            ...event,
            display_date: dateStr,
            is_start: i === 0,
            is_end: i === duration - 1,
            is_middle: i > 0 && i < duration - 1,
            day_position: i + 1,
            total_days: duration
          });
        }
      });

      return expandedEvents;
    },
    date_hover: {
      get() {
        return this.$store.state.rate.date_hover;
      },
      set(value) {
        this.$store.dispatch("setRateDateHover", value);
      }
    },
    day_hover: {
      get() {
        return this.$store.state.rate.day_hover;
      },
      set(value) {
        this.$store.dispatch("setRateDayHover", value);
      }
    },
    ...mapState({
      calendar_store: state => state.calendar
    })
  },
  methods: {
    mustRender(date) {
      // Must render if date inside days_to_render
      return this.days_to_render.indexOf(date) >= 0;
    },
    getDayIndexInRendered(day) {
      return this.days_to_render.indexOf(day);
    },
    getEventsForDay(day) {
      // Devolver todos los eventos que ocurren en este día específico, ordenados por importancia
      const events = this.processedEvents.filter(
        event => event.display_date === day
      );

      // Ordenar por importancia (de mayor a menor)
      return events.sort((a, b) => {
        const impactOrder = {
          exceptional: 5,
          very_high: 4,
          high: 3,
          medium: 2,
          low: 1
        };

        const impactA = impactOrder[a.impact] || 0;
        const impactB = impactOrder[b.impact] || 0;

        return impactB - impactA; // Orden descendente (mayor importancia primero)
      });
    },
    isWeekend(date) {
      const dayOfWeek = Vue.moment(date).day();
      return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Domingo, 6 = Sábado
    },
    getImpactColor(impact) {
      return this.calendar_store.impacts.find(i => i.value === impact)
        ? this.calendar_store.impacts.find(i => i.value === impact).color
        : "grey";
    },
    getEventTypeIcon(type) {
      return Utils.getEventTypeIcon(type);
    },
    truncateEventName(name, maxLength = 15) {
      if (!name) return "";
      if (name.length <= maxLength) return name;
      return name.substring(0, maxLength - 3) + "...";
    },
    getEventDateRange(event) {
      const startDate = Vue.moment(event.start_date).format("DD/MM/YYYY");
      const endDate = Vue.moment(event.end_date).format("DD/MM/YYYY");
      const dateRange =
        event.start_date === event.end_date
          ? startDate
          : `${startDate} - ${endDate}`;
      return dateRange;
    },
    formatImpact(impact) {
      return this.calendar_store.impacts.find(i => i.value === impact)
        ? this.calendar_store.impacts.find(i => i.value === impact).text
        : impact;
    },
    formatType(type) {
      return this.calendar_store.types.find(t => t.value === type)
        ? this.calendar_store.types.find(t => t.value === type).text
        : type;
    },
    getMostImportantEvent(day) {
      const events = this.getEventsForDay(day);
      if (events.length > 0) {
        return events[0];
      }
      return null;
    },
    getAdditionalEventsCount(day) {
      const events = this.getEventsForDay(day);
      return events.length - 1;
    },
    getAdditionalEvents(day) {
      const events = this.getEventsForDay(day);
      return events.slice(1);
    },
    getMostImportantAdditionalEvent(day) {
      const events = this.getEventsForDay(day);
      if (events.length > 1) {
        // Como los eventos ya están ordenados por importancia en getEventsForDay(),
        // el segundo evento (índice 1) será el más importante de los adicionales
        return events[1];
      }
      return null;
    }
  }
};
</script>

<style lang="scss">
#rate-hotel-events {
  .counter {
    font-size: 10px;
  }
}
</style>
